import { isApiError, unwrap, type HiveClient, type WizardData, type WizardFieldError, type WizardSchema } from '@hive/api';
import type { Lang } from '@hive/i18n';
import { assertStepLimit, fieldOptions, useLoad, type OptionsByType, type WizardDraftStore } from '@hive/ui';
import { useMemo } from 'react';
import { useI18nP, type PlatformKey } from '../i18n';
import { useSession } from './session';

/**
 * Ponte entre o motor de assistentes da UI e a API de assistentes do núcleo (HU-015):
 * esquema, validação localizada por passo, rascunho no servidor (sem segredos) e envio.
 */
export function useWizardSchema(id: string, catalogId?: string) {
  const { api } = useSession();
  return useLoad(async () => {
    const s = (await unwrap(api.GET('/api/v1/wizards/{id}', { params: { path: { id }, query: { catalogId } } }))) as unknown as WizardSchema;
    assertStepLimit(s);
    return s;
  }, [api, id, catalogId]);
}

/**
 * Contorno da lacuna 1 do tracker: o validador do núcleo recusa selects cujas opções vêm como
 * texto simples (campos do catálogo). Se o valor está entre as opções, o erro é descartado.
 */
export function dropFalseSelectErrors(schema: WizardSchema, data: WizardData, errors: Record<string, WizardFieldError>) {
  const out = { ...errors };
  for (const s of schema.steps)
    for (const f of s.fields ?? []) {
      if (f.type !== 'select' || !out[f.id]) continue;
      const plain = (f.options ?? []).some((o) => typeof o === 'string');
      if (plain && fieldOptions(f, (v) => String(v ?? '')).some((o) => o.value === String(data[f.id]))) delete out[f.id];
    }
  return out;
}

export function serverValidate(api: HiveClient, schema: WizardSchema, lang: Lang, catalogId?: string) {
  return async (stepId: string, data: WizardData) => {
    const r = await unwrap(
      api.POST('/api/v1/wizards/{id}/validate', { params: { path: { id: schema.id }, query: { catalogId } }, body: { step: stepId, data, lang } }),
    );
    const errs = (r.errors ?? {}) as Record<string, WizardFieldError>;
    return dropFalseSelectErrors(schema, data, errs);
  };
}

const LOCAL = (id: string, catalogId?: string) => `hive.wz.${id}${catalogId ? ':' + catalogId : ''}`;

/** Guarda local de cada mudança (sessionStorage, sem segredos) — retoma depois de um novo login. */
export function autosave(id: string, catalogId?: string) {
  return (step: number, data: WizardData) => {
    try {
      window.sessionStorage.setItem(LOCAL(id, catalogId), JSON.stringify({ step, data, at: new Date().toISOString() }));
    } catch {
      /* sem armazenamento */
    }
  };
}

export function clearAutosave(id: string, catalogId?: string) {
  try {
    window.sessionStorage.removeItem(LOCAL(id, catalogId));
  } catch {
    /* sem armazenamento */
  }
}

export function draftStore(api: HiveClient, id: string, catalogId?: string): WizardDraftStore {
  return {
    async load() {
      let local: { step: number; data: WizardData; at: string } | null;
      try {
        const raw = window.sessionStorage.getItem(LOCAL(id, catalogId));
        local = raw ? JSON.parse(raw) : null;
      } catch {
        local = null;
      }
      let server: { step: number; data: WizardData; updatedAt?: string } | null = null;
      try {
        const d = await unwrap(api.GET('/api/v1/wizards/{id}/draft', { params: { path: { id } } }));
        const data = (d.data ?? {}) as WizardData;
        // O rascunho do servidor é por assistente; o de outra integração não serve aqui.
        if (!catalogId || data._catalogId === catalogId) server = { step: d.step ?? 0, data, updatedAt: d.updatedAt };
      } catch (e) {
        if (!(isApiError(e) && e.status === 404)) throw e;
      }
      // Fica o mais recente (o guardado local cobre a sessão que caiu no meio do preenchimento).
      if (local && (!server || !server.updatedAt || local.at > server.updatedAt)) {
        const hasData = Object.values(local.data).some((v) => v !== '' && v != null && !(Array.isArray(v) && !v.length));
        if (hasData) return { step: local.step, data: local.data, updatedAt: local.at };
      }
      if (server) {
        const { _catalogId: _c, ...rest } = server.data;
        return { ...server, data: rest };
      }
      return null;
    },
    async save(step, data) {
      await unwrap(api.PUT('/api/v1/wizards/{id}/draft', { params: { path: { id }, query: { catalogId } }, body: { step, data } }));
    },
    async discard() {
      clearAutosave(id, catalogId);
      await unwrap(api.DELETE('/api/v1/wizards/{id}/draft', { params: { path: { id } } })).catch(() => undefined);
    },
  };
}

export async function submitWizard<R>(api: HiveClient, id: string, data: WizardData, lang: Lang, catalogId?: string): Promise<R> {
  const r = await unwrap(api.POST('/api/v1/wizards/{id}/submit', { params: { path: { id }, query: { catalogId } }, body: { data, lang } }));
  clearAutosave(id, catalogId);
  return r as R;
}

/** Rótulo de uma permissão e da área dela (o catálogo da API traz só a chave). */
export function permLabel(t: (k: PlatformKey) => string, has: (k: string) => boolean, key: string) {
  const k = `perm.${key}`;
  return has(k) ? t(k as PlatformKey) : key;
}
export function areaLabel(t: (k: PlatformKey) => string, has: (k: string) => boolean, area: string) {
  const k = `area.${area}`;
  return has(k) ? t(k as PlatformKey) : area;
}
export function scopeLabel(t: (k: PlatformKey) => string, has: (k: string) => boolean, scope: string) {
  const k = `scope.${scope}`;
  return has(k) ? t(k as PlatformKey) : scope;
}

/** Nome do perfil: os padrões são traduzidos pela chave; os criados pela empresa ficam como estão. */
export function profileName(t: (k: PlatformKey) => string, has: (k: string) => boolean, p: { key?: string; name?: string; builtin?: boolean } | null | undefined) {
  if (!p) return '';
  const k = `profile.${p.key}`;
  return p.key && has(k) && (p.builtin !== false || !p.name) ? t(k as PlatformKey) : p.name ?? p.key ?? '';
}

/** Opções que dependem da empresa: perfis, permissões (agrupadas por área) e domínios da API. */
export function useDynamicOptions(schema: WizardSchema | undefined) {
  const { api } = useSession();
  const { t, has } = useI18nP();
  const types = useMemo(() => new Set((schema?.steps ?? []).flatMap((s) => (s.fields ?? []).map((f) => f.type))), [schema]);
  const loaded = useLoad(async () => {
    const out: { profiles?: Awaited<ReturnType<typeof loadProfiles>>; perms?: Awaited<ReturnType<typeof loadPerms>>; scopes?: string[] } = {};
    if (types.has('profile-multi')) out.profiles = await loadProfiles(api);
    if (types.has('permission-multi')) out.perms = await loadPerms(api);
    if (types.has('api-scope-multi')) out.scopes = await unwrap(api.GET('/api/v1/api-accounts/scopes'));
    return out;
  }, [api, types]);
  const options = useMemo<OptionsByType>(() => {
    const d = loaded.data;
    if (!d) return {};
    return {
      'profile-multi': d.profiles?.map((p) => ({ value: p.id ?? '', label: profileName(t, has, p), desc: p.privileged ? t('users.privileged') : undefined })),
      'permission-multi': d.perms?.map((p) => ({ value: p.key ?? '', label: permLabel(t, has, p.key ?? ''), desc: p.key, group: p.area })),
      'api-scope-multi': d.scopes?.map((s) => ({ value: s, label: scopeLabel(t, has, s) })),
    };
  }, [loaded.data, t, has]);
  return { options, loading: loaded.loading, error: loaded.error, groupLabel: (g: string) => areaLabel(t, has, g) };
}

function loadProfiles(api: HiveClient) {
  return unwrap(api.GET('/api/v1/profiles'));
}
function loadPerms(api: HiveClient) {
  return unwrap(api.GET('/api/v1/permissions'));
}
